"use strict";
/** @format */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLabel = exports.DateRangePicker = void 0;
const react_1 = __importDefault(require("react"));
const x_date_pickers_1 = require("@mui/x-date-pickers");
const x_date_pickers_pro_1 = require("@mui/x-date-pickers-pro");
const AdapterMoment_1 = require("@mui/x-date-pickers/AdapterMoment");
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const Button_1 = __importDefault(require("../Button/Button"));
const DatePicker = (props) => (react_1.default.createElement(x_date_pickers_1.LocalizationProvider, { dateAdapter: AdapterMoment_1.AdapterMoment },
    react_1.default.createElement(DateRangePicker, { ...props })));
function DateRangePicker(props) {
    const [isOpen, setOpen] = react_1.default.useState(false);
    const [anchorEl, setAnchorEl] = react_1.default.useState(null);
    const { timezone, value, onChange, useButtonField, shortcutItems, ...dateRangePickerProps } = props;
    // @ts-ignore
    const [startValue, endValue] = value;
    // @ts-ignore
    const valueInTimezone = [toTimezone(startValue, timezone), toTimezone(endValue, timezone)];
    // If null or undefined is provided as a starting value, the callback
    // will not provide the selected range in the requested timezone, since
    // it uses the input to determine the timezone. So we force the timezone
    const handleChange = ([startRange, endRange], validationError) => {
        // Drop the attached timezone. Do not use the timezone force, as it has DST issues
        const rangeInTimezone = [
            replaceTimezone(startRange, timezone),
            replaceTimezone(endRange, timezone),
        ];
        onChange?.(rangeInTimezone, validationError);
    };
    return (react_1.default.createElement(x_date_pickers_1.LocalizationProvider, { dateAdapter: AdapterMoment_1.AdapterMoment },
        react_1.default.createElement(x_date_pickers_pro_1.DateRangePicker, { ...dateRangePickerProps, slots: useButtonField
                ? {
                    field: DateRangeButtonField,
                }
                : undefined, slotProps: {
                actionBar: useButtonField ? { actions: ['cancel', 'accept'] } : undefined,
                field: useButtonField
                    ? { setOpen, setAnchorEl, shortcutItems: shortcutItems }
                    : undefined,
                layout: !!shortcutItems
                    ? {
                        sx: {
                            '.MuiPickersDay-root': {
                                '&.Mui-selected': {
                                    backgroundColor: '#004A5B',
                                },
                            },
                            '.MuiDateRangePickerDay-root': {
                                '&.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
                                    backgroundColor: '#D6F4F3',
                                },
                            },
                            '.MuiDateRangeCalendar-monthContainer': {
                                '.MuiPickersArrowSwitcher-root .MuiTypography-subtitle1': {
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: '610',
                                    lineHeight: '20px',
                                },
                            },
                            '.MuiDateRangeCalendar-monthContainer:first-of-type .MuiPickersArrowSwitcher-root button:last-of-type': {
                                display: 'none',
                            },
                            '.MuiDateRangeCalendar-monthContainer:last-of-type .MuiPickersArrowSwitcher-root button:first-of-type': {
                                color: 'blue',
                                display: 'none',
                            },
                            [`.${x_date_pickers_1.pickersLayoutClasses.shortcuts}`]: {
                                gridColumn: 3,
                                gridRow: 2,
                            },
                        },
                    }
                    : undefined,
                popper: useButtonField
                    ? {
                        anchorEl: anchorEl,
                    }
                    : undefined,
                shortcuts: shortcutItems
                    ? {
                        changeImportance: 'set',
                        items: shortcutItems,
                    }
                    : undefined,
            }, open: useButtonField ? isOpen : undefined, onClose: useButtonField ? () => setOpen(false) : undefined, onOpen: useButtonField ? () => setOpen(true) : undefined, value: valueInTimezone, onChange: handleChange })));
}
exports.DateRangePicker = DateRangePicker;
// If we create a moment using null, the field will be flagged with an error
// So only convert defined values to a moment timezone object
function toTimezone(value, timezone) {
    if (value === null || value === undefined) {
        return value;
    }
    if (!timezone) {
        return value;
    }
    return moment_timezone_1.default.tz(value, timezone);
}
const DATE_ONLY_FORMAT = 'YYYY-MM-DD';
function replaceTimezone(value, timezone) {
    if (!value || !timezone) {
        return value;
    }
    // Print date and then re-parse in the expected timezone.
    // DO NOT USE keepLocale tz conversion it has known bugs with DST
    return moment_timezone_1.default.tz(value.format(DATE_ONLY_FORMAT), DATE_ONLY_FORMAT, timezone);
}
exports.default = DatePicker;
const DateRangeButtonField = (props) => {
    const { setOpen, shortcutItems, value } = props;
    const label = (0, exports.getLabel)(value, shortcutItems);
    const handleClick = (e) => {
        // This component does not properly set a ref passed through the slotProps, so we need to set it via useState so that we can set it as an anchorEl for the popper
        props.setAnchorEl?.((prev) => {
            if (!prev) {
                return e.currentTarget;
            }
            return prev;
        });
        setOpen?.((prev) => !prev);
    };
    return (react_1.default.createElement(Button_1.default, { variant: "outlined", onClick: handleClick }, label));
};
const getLabel = (value, shortcutItems) => {
    const shortcutLabel = shortcutItems.find((item) => {
        const itemValue = item.getValue({ isValid: () => true });
        return value && itemValue[0]?.isSame(value[0], 'day') && itemValue[1]?.isSame(value[1], 'day');
    })?.label;
    if (shortcutLabel) {
        return shortcutLabel;
    }
    if (!value || (!value[0] && !value[1])) {
        return 'Select Date Range';
    }
    const [start, end] = value;
    const startLabel = !!start && start.format('MMM DD');
    const endLabel = !!end && end.format('MMM DD');
    if (startLabel && endLabel && startLabel !== endLabel) {
        return `${startLabel} - ${endLabel}`;
    }
    return startLabel || endLabel;
};
exports.getLabel = getLabel;
DateRangeButtonField.displayName = 'DateRangeButtonField';
